import { Inject, Injectable, Optional } from '@angular/core';
import { AbstractFormContext, FormContextToken } from 'app.services/dynamic.forms/wizard/services/form.context/abstract.form.context';
import { LogCat, LogManager } from 'common';
import * as _ from "lodash";
import { MetadataService } from "../../../../app.services/clinical/metadata/services/metadata.service";
import { PathEvaluator } from './path.evaluator';

export const WebPathToDS = [
	{
		'path': '',
		'dataSet': 'PatientDetail',
		'dataTable': ['PERSON', 'PATIENT']
	},
	{
		'path': 'visit[0]',
		'dataSet': 'VisitDetail',
		'dataTable': ['VISIT']
	},
	{
		'path': 'visit[0].orders[0]',
		'dataSet': 'OrderDetail',
		'dataTable': ['ORDERS']
	},
	{
		'path': 'visit[0].orders[0].requestedProcedure[0].sps[0]',
		'dataSet': 'SPSDetail',
		'dataTable': ['SPS']
	},
	{
		'path': 'visit[0].orders[0].requestedProcedure[0].sps[0].spsXPps[0].pps[0]',
		'dataSet': 'PPSDetail',
		'dataTable': ['PPS']
	},
	{
		'path': 'visit[0].orders[0].requestedProcedure[0].sps[0].spsXPps[0].pps[0].message[0]',
		'dataSet': 'MessagingDetail',
		'dataTable': ['MESSAGE']
	},
	{
		'path': 'visit[0].orders[0].requestedProcedure[0].sps[0].spsXPps[0].pps[0].report[0]',
		'dataSet': 'ReportDetail',
		'dataTable': ['REPORT']
	}
];

@Injectable({ providedIn: 'root' })
  export class DatasetWebConverter {
	constructor(protected metadataService: MetadataService,
				protected pathEvaluator: PathEvaluator,
				@Optional() @Inject(FormContextToken) public formContextService: AbstractFormContext)
				 {
	}
	public dbCaseFunc= (x) => _.snakeCase(x).toUpperCase();
	public webCaseFunc = (x) => _.camelCase(x);

	public displayTableName(path: string, displayColumn: boolean = false): string {
		if (!path) path = '';
		let fieldName;
		let table = this.getTableFromFlatMetadata(path);
		if (!table) {
			LogManager.debug(LogCat.Metadata.General, 'Table not found for path: ' + path);
		} else {
			if (displayColumn) {
				path = this.pathEvaluator.normalizeWildcardPath(path);
				if (table.fields.find(f => f.fullPath === path)) {
					fieldName = (path.lastIndexOf(".") >= 0) ? path.substring(path.lastIndexOf(".") + 1) : path
				}
			}
		}
		if(table) {
			return this.dbCaseFunc(table.tableName) +
			(fieldName ? '.' + this.dbCaseFunc(fieldName) : '') +
			(table.level ? ' (' + this.dbCaseFunc(table.level) + ')' : '')
		}else{
			return displayColumn ? this.GetFullNameAsDBCaseFromWebPath(path) : this.GetTableNameAsDBCaseFromWebPath(path);
		}
	}

	public GetTableNameFromWebPath(dsPath:string, defaultValue:string = 'patient') :string {
		const fkLookupPartIndex = dsPath.indexOf('FKLookupRows');
		if (fkLookupPartIndex > -1) {
			//Remove the FKLookupRows part so that it is considered as a single field
			dsPath = dsPath.slice(0, fkLookupPartIndex);
		}

		let lastDotIndex = dsPath.lastIndexOf(".");
		let tableName:string;

		if (lastDotIndex >= 0) {
			let almostLastDotIndex = dsPath.lastIndexOf(".",lastDotIndex-1);
			if (almostLastDotIndex >= 0) {
				tableName = dsPath.substring(almostLastDotIndex+1, lastDotIndex-3);
			} else {
				tableName = dsPath.substring(0, lastDotIndex-3);
			}
		} else {
			tableName = defaultValue;
		}

		return tableName;
	}


	public GetFieldNameFromWebPath(dsPath:string) {
		const fkLookupPartIndex = dsPath.indexOf('FKLookupRows');
		if (fkLookupPartIndex > -1) {
			//Remove nested FKLookuprows part, is it exists
			const lastIndexOfFKLookupRows = dsPath.lastIndexOf('FKLookupRows');
			if (lastIndexOfFKLookupRows >  fkLookupPartIndex) {
				dsPath = dsPath.slice(0, lastIndexOfFKLookupRows)
			}

			//Remove the FKLookupRows part and the lookup column so we consider the field only
			dsPath = dsPath.replace('.FKLookupRows', '');
			const lastIndexOfDot = dsPath.lastIndexOf('.');
			dsPath = dsPath.slice(0, lastIndexOfDot);
		}

		let lastDotIndex = dsPath.lastIndexOf(".");
		let fieldName:string;

		if (lastDotIndex >= 0) {
			fieldName = dsPath.substring(lastDotIndex+1);
		} else {
			fieldName = dsPath;
		}
		return fieldName;
	}

	public GetLookupColumnNameFromPath(path: string): string {
		if (path?.includes('FKLookupRows')) {
			let parts = path.split('.');
			return this.dbCaseFunc(parts[parts.length - 1]);
		} else {
			return null;
		}
	}

	public GetFieldNameAsDBCaseFromWebPath(dsPath:string) {
		return this.dbCaseFunc(this.GetFieldNameFromWebPath(dsPath));
	}

	public GetTableNameAsDBCaseFromWebPath(dsPath:string, defaultValue:string = 'patient') :string {
		return this.dbCaseFunc(this.GetTableNameFromWebPath(dsPath, defaultValue));
	}

	public GetFullNameAsDBCaseFromWebPath(dsPath:string, defaultValue:string = 'patient') {
		return this.GetTableNameAsDBCaseFromWebPath(dsPath, defaultValue) + "." +
			this.GetFieldNameAsDBCaseFromWebPath(dsPath);
	}

	/**
	 * Table name may NOT be a unique identifier, if you are not 100% sure that the table name is unique,
	 * please use getTableNameFromWebPath instead
	 */
	public getWebPathFromDS(tableName, fieldName) {
		let tableNameInWebCase = _.camelCase(tableName);

		if (tableNameInWebCase === 'person') {
			tableNameInWebCase = 'patient';
		}

		let table = this.metadataService.flatPatientMetadata.find(x => x.tableName === tableNameInWebCase);

		if (!table) {
			return null;
		}

		let field = table.fields.find(x => this.GetFieldNameAsDBCaseFromWebPath(x.fullPath) === fieldName);

		if (!field) {
			return null;
		}

		return field.fullPath;
	}

	public getTableFromFlatMetadata(path: string) {
		//remove the FKLookupRows part if it exists, otherwise the table will not be found, not relevant to retrieve the table
		if (path.indexOf('FKLookupRows') === 0) {
			//patient level
			path = '';
		}else if (path.indexOf('.FKLookupRows') > 0) {
			path = path.slice(0, path.indexOf('.FKLookupRows'));
		}
		//remove [0] or [*] , this can be happen when request arrive from togheter manager, and in anycase
		//we don't have brackets at the end of the path in metadata
		if (path.endsWith(']')) {
			path = path.slice(0, path.lastIndexOf('['));
		}
		//remove wildcards, in metadata we don't have wildcards in path but only [0]
		path = this.pathEvaluator.normalizeWildcardPath(path);
		let customFlatMetadata = this.formContextService?.flatMetadata ?? this.metadataService.flatPatientMetadata;
		let table = customFlatMetadata.find(
			x => x.fullPath === path || (path === '' && x.fullPath === 'Patient') || x.fields.some(f => f.fullPath === path));
		return table;
	}

	public getMetadataFullPathFromWebPath(path: string, fieldName?: string) {
		let table = this.getTableFromFlatMetadata(path);
		fieldName = fieldName ? this.webCaseFunc(fieldName) : null;
		if (table) {
			if (fieldName) {
				let field = table.fields.find(x => this.GetFieldNameFromWebPath(x.fullPath) === fieldName);
				if (!field) {
					return null;
				}
				return field.fullPath;
			} else {
				return table.fullPath;
			}
		} else {
			return null;
		}
	}

	public covertDBCaseToWebCase(dbCaseName: string): string {
		let webCaseName = _.camelCase(dbCaseName);
		return webCaseName;
	}

	public getDSObjectFromWebPath(webPath: string) : {dataSet:string, dataTable:string, dataField:string} {
		let path, dataSet, dataTable, dataField = '';

		for (let item of WebPathToDS) {
			if (webPath.indexOf(item.path) === 0) {
				path = item.path;
				dataSet = item.dataSet;
				dataTable = item.dataTable[0];
			}
		}

		let webPathParts = webPath.replace(path, '').split('.').filter(x => x !== '');

		if (webPathParts.length === 1) {
			dataField = this.dbCaseFunc(webPathParts[0]);
		} else {
			dataTable =  this.dbCaseFunc(webPathParts[webPathParts.length - 2].replace('[0]', ''));
			dataField =  this.dbCaseFunc(webPathParts[webPathParts.length - 1]);
		}

		return {dataSet, dataTable, dataField};
	}	
}
